import { useEffect, useState } from 'react';
import { apiFetch, API_URL } from '../utils/api';

const categories = [
  { key: 'whatsapp_instagram', label: 'WhatsApp + Instagram' },
  { key: 'facebook_dm', label: 'Facebook DM' },
  { key: 'after_hours_calls', label: 'After-hours Calls' },
  { key: 'missed_calls', label: 'Missed Calls' },
] as const;

type PoolKey = typeof categories[number]['key'];
type Agent = { id: string; name: string; email: string; pools: Record<PoolKey, boolean> };
type SettingsResponse = { initialized: boolean; agents: Agent[]; error?: string };
type BlockedNumber = { phone_identity: string; reason: string | null; blocked_at: string; blocked_by: string; updated_at: string };

export default function AdminPanel({ onClose }: { onClose: () => void }) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [blockedNumbers, setBlockedNumbers] = useState<BlockedNumber[]>([]);
  const [blockedPhone, setBlockedPhone] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [blockedSaving, setBlockedSaving] = useState(false);
  const [blockedSearch, setBlockedSearch] = useState('');
  const [debouncedBlockedSearch, setDebouncedBlockedSearch] = useState('');
  const [activeSection, setActiveSection] = useState<'assignment' | 'blocked'>('assignment');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedBlockedSearch(blockedSearch.trim().toLowerCase()), 250);
    return () => window.clearTimeout(timer);
  }, [blockedSearch]);

  const visibleBlockedNumbers = blockedNumbers.filter((row) => {
    if (!debouncedBlockedSearch) return true;
    return [row.phone_identity, row.reason || '', row.blocked_by]
      .some((value) => value.toLowerCase().includes(debouncedBlockedSearch));
  });

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_URL}/api/admin/agent-assignment-pools`);
      const data = await response.json() as SettingsResponse;
      if (!response.ok) throw new Error(data.error || 'Could not load assignment settings');
      setAgents(data.agents);
      setInitialized(data.initialized);
      const blockedResponse = await apiFetch(`${API_URL}/api/admin/blocked-numbers`);
      const blockedData = await blockedResponse.json() as { blocked_numbers?: BlockedNumber[]; error?: string };
      if (!blockedResponse.ok) throw new Error(blockedData.error || 'Could not load blocked numbers');
      setBlockedNumbers(blockedData.blocked_numbers || []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load assignment settings');
    } finally {
      setLoading(false);
    }
  };

  const blockNumber = async () => {
    setBlockedSaving(true); setError(''); setSuccess('');
    try {
      const response = await apiFetch(`${API_URL}/api/admin/blocked-numbers`, {
        method: 'POST', body: JSON.stringify({ phone: blockedPhone, reason: blockedReason || undefined }),
      });
      const data = await response.json() as { error?: string };
      if (!response.ok) throw new Error(data.error || 'Could not block number');
      setBlockedPhone(''); setBlockedReason(''); await loadSettings(); setSuccess('Number blocked.');
    } catch (saveError) { setError(saveError instanceof Error ? saveError.message : 'Could not block number'); }
    finally { setBlockedSaving(false); }
  };

  const unblockNumber = async (phone: string) => {
    setBlockedSaving(true); setError(''); setSuccess('');
    try {
      const response = await apiFetch(`${API_URL}/api/admin/blocked-numbers/unblock`, { method: 'POST', body: JSON.stringify({ phone }) });
      const data = await response.json() as { error?: string };
      if (!response.ok) throw new Error(data.error || 'Could not unblock number');
      await loadSettings(); setSuccess('Number unblocked.');
    } catch (saveError) { setError(saveError instanceof Error ? saveError.message : 'Could not unblock number'); }
    finally { setBlockedSaving(false); }
  };

  useEffect(() => { void loadSettings(); }, []);

  const updatePool = async (agent: Agent, poolKey: PoolKey, enabled: boolean) => {
    const requestKey = `${agent.id}:${poolKey}`;
    setSaving(requestKey);
    setError('');
    setSuccess('');
    try {
      const response = await apiFetch(`${API_URL}/api/admin/agent-assignment-pools`, {
        method: 'PUT',
        body: JSON.stringify({ changes: [{ userId: agent.id, poolKey, enabled, expectedEnabled: agent.pools[poolKey] }] }),
      });
      const data = await response.json() as { error?: string };
      if (!response.ok) throw new Error(data.error || 'Could not save assignment setting');
      setAgents((current) => current.map((row) => row.id === agent.id ? { ...row, pools: { ...row.pools, [poolKey]: enabled } } : row));
      setSuccess('Assignment setting saved.');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save assignment setting');
      if (saveError instanceof Error && /concurrently/i.test(saveError.message)) void loadSettings();
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <h1 className="text-lg font-semibold text-slate-900">Admin Panel</h1>
          <button type="button" onClick={onClose} autoFocus className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600">
            Back to CRM
          </button>
        </div>
      </header>
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col md:flex-row">
        <aside className="border-b border-slate-200 bg-white p-4 md:w-60 md:flex-none md:border-b-0 md:border-r">
          <nav aria-label="Admin modules">
            <button type="button" onClick={() => setActiveSection('assignment')} aria-current={activeSection === 'assignment' ? 'page' : undefined} className={`block w-full rounded-lg px-3 py-2.5 text-left text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 ${activeSection === 'assignment' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'}`}>
              Auto-Assignment Settings
            </button>
            <button type="button" onClick={() => setActiveSection('blocked')} aria-current={activeSection === 'blocked' ? 'page' : undefined} className={`mt-2 block w-full rounded-lg px-3 py-2.5 text-left text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 ${activeSection === 'blocked' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'}`}>Blocked Numbers</button>
          </nav>
        </aside>
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6">
          {activeSection === 'assignment' && <section id="auto-assignment-settings" aria-labelledby="auto-assignment-heading" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 id="auto-assignment-heading" className="text-base font-semibold text-slate-900">Auto-Assignment Settings</h2>
              <p className="mt-1 text-sm text-slate-500">Changes apply to future automatic assignments. Existing lead ownership is unchanged.</p>
            </div>
            <div className="space-y-3 px-5 py-4" aria-live="polite">
              {!initialized && !loading && !error && <p className="text-sm text-amber-800">Assignment settings are not initialized. Existing assignment pools must be verified and imported before editing.</p>}
              {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
              {success && <p className="text-sm text-emerald-700">{success}</p>}
            </div>
            <div className="overflow-x-auto" role="region" aria-label="Agent assignment settings" tabIndex={0}>
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-600">
                  <tr>
                    <th scope="col" className="px-5 py-3">Agent Name</th>
                    <th scope="col" className="px-5 py-3">Agent Email</th>
                    {categories.map(({ key, label }) => <th key={key} scope="col" className="px-4 py-3 text-center">{label}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500">Loading agents…</td></tr>
                    : agents.length === 0 ? <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-500">No active agents found.</td></tr>
                      : agents.map((agent) => (
                        <tr key={agent.id} className="hover:bg-slate-50">
                          <th scope="row" className="whitespace-nowrap px-5 py-4 font-medium text-slate-900">{agent.name}</th>
                          <td className="px-5 py-4 text-slate-500">{agent.email}</td>
                          {categories.map(({ key, label }) => {
                            const requestKey = `${agent.id}:${key}`;
                            return <td key={key} className="px-4 py-4 text-center">
                              <input type="checkbox" aria-label={`${agent.name}: ${label}`} checked={agent.pools[key]} disabled={!initialized || Boolean(saving)}
                                onChange={(event) => void updatePool(agent, key, event.target.checked)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-emerald-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
                                aria-busy={saving === requestKey} />
                            </td>;
                          })}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </section>}
          {activeSection === 'blocked' && <section id="blocked-numbers" aria-labelledby="blocked-numbers-heading" className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 id="blocked-numbers-heading" className="text-base font-semibold text-slate-900">Blocked Numbers</h2>
              <p className="mt-1 text-sm text-slate-500">Blocked numbers are prevented from new CRM activity. Historical records remain visible.</p>
            </div>
            <form className="flex flex-wrap gap-3 px-5 py-4" onSubmit={(event) => { event.preventDefault(); void blockNumber(); }}>
              <input value={blockedPhone} onChange={(event) => setBlockedPhone(event.target.value)} placeholder="Indian phone number" aria-label="Phone number to block" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" required />
              <input value={blockedReason} onChange={(event) => setBlockedReason(event.target.value)} placeholder="Reason (optional)" aria-label="Block reason" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <button type="submit" disabled={blockedSaving} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{blockedSaving ? 'Saving…' : 'Block'}</button>
            </form>
            <div className="relative px-5 pb-4">
              <input
                value={blockedSearch}
                onChange={(event) => setBlockedSearch(event.target.value)}
                placeholder="Search blocked numbers, reason, or admin"
                aria-label="Search blocked numbers"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              {blockedSearch.trim() && debouncedBlockedSearch && visibleBlockedNumbers.length > 0 && (
                <div className="absolute left-5 right-5 top-full z-10 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg" role="listbox" aria-label="Blocked number suggestions">
                  {visibleBlockedNumbers.slice(0, 6).map((row) => (
                    <button key={row.phone_identity} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onMouseDown={() => setBlockedSearch(row.phone_identity)}>
                      <span className="font-medium text-slate-900">{row.phone_identity}</span>
                      {row.reason && <span className="ml-2 text-slate-500">{row.reason}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs font-semibold text-slate-600"><tr><th className="px-5 py-3">Phone identity</th><th className="px-5 py-3">Reason</th><th className="px-5 py-3">Blocked by</th><th className="px-5 py-3">Action</th></tr></thead>
                <tbody className="divide-y divide-slate-100">{blockedNumbers.length === 0 ? <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500">No active blocked numbers.</td></tr> : visibleBlockedNumbers.length === 0 ? <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500">No matching blocked numbers.</td></tr> : visibleBlockedNumbers.map((row) => <tr key={row.phone_identity}><td className="px-5 py-3">{row.phone_identity}</td><td className="px-5 py-3">{row.reason || '—'}</td><td className="px-5 py-3">{row.blocked_by}</td><td className="px-5 py-3"><button type="button" onClick={() => void unblockNumber(row.phone_identity)} disabled={blockedSaving} className="text-sm font-semibold text-emerald-700 disabled:opacity-50">Unblock</button></td></tr>)}</tbody>
              </table>
            </div>
          </section>}
        </main>
      </div>
    </div>
  );
}
